import * as THREE from 'three';
import { FishBatch, facesRight, fishAtlas, type FishDraw } from '../fishBatch';
import { hash3 } from '../fishSwim';
import { pickNearest } from '../pick';
import { DEEP7_SPRITES } from '../../ui/data/deep7Sprites';
import { DEEP7_SPECIES, type Deep7SpeciesId } from '../../sim/bottomfish';
import { VISITOR_DRAW } from './visitors';

/**
 * The Deep 7 on the Slope's floor (M215, the user's word: "maybe you can change the upper slope to just the slope so
 * that it includes the deep 7?"): the day's fish of each species in the scene's hectare (`WaterSceneData.bottomfish`),
 * each its own sprite, the user's picture, at its species' mean size. As they are seen: "the target species are known
 * to school in the water column several meters above the bottom and higher and are known to favor steep and rocky
 * slopes" (Richards et al. 2016, NOAA TM NMFS-PIFSC-53, p. 9, public domain), in "aggregations of up to 100
 * individuals" of one species "well off the bottom (20 m) in the vicinity of underwater headlands and promontories"
 * (Ralston, Gooding and Ludwig 1986, *Fishery Bulletin* 84, p. 149, at Johnston Atoll), the kalekale's "thick
 * aggregations on the bottom" (Richards et al. 2016, Fig. 15's caption, p. 54).
 *
 * Each a rendering choice (JUDGED unless said):
 * - Each species one group of its own about a point of the floor, its fish milling round it, each on a ring of its
 *   own, `ringM` wide for each of the group's fish's square root, at `lengthsPerS` of their lengths a second, rising
 *   and falling `bobM` over `bobS`.
 * - Its group's place by its depths (`DEEP7_SPECIES.depthM`): the shallower nearer the home view's eye, up the slope,
 *   the deeper farther down it, `alongM` m from the nearest to the farthest, across the view by a draw of its own
 *   within `acrossM` either way. The scene's floor is drawn at the Slope's middle (`BenthicScene`), so the order is
 *   their depths', not the meters between them (the ʻōpakapaka's 150 m and the ehu's 255 m drawn 12 m apart).
 * - Each fish `aboveM` over the floor, a draw of its own between the two: "several meters" (above), drawn under the
 *   "20 m" of Johnston Atoll so the home view, 5.5 m over the floor, sees them; the kalekale's `lowM`.
 * - Drawn as long as the visitors draw the reef fish (`VISITOR_DRAW.fishTimes`, 1.75 times), at their fork length.
 * - Drawn solid, writing their depth, as the coral colonies are (M164), so a visitor beyond a fish is hidden by it and
 *   one nearer is drawn over it; none written while a fish is less than half seen, fading in or out over `fadeS`.
 */
export const SLOPE_FISH = { ringM: 0.9, lengthsPerS: 0.3, bobM: 0.4, bobS: 20, alongM: 12, acrossM: 6, aboveM: [2, 8], lowM: [0.6, 2.5], fadeS: 2 } as const;

/** A species' fish in the scene's hectare today, and its mean size (`deep7MeanSize`). */
export interface SlopeFishCount {
  species: Deep7SpeciesId;
  count: number;
  forkCm: number;
}

/** A Deep 7 fish as drawn, for its tap and its card. */
export interface SlopeFishDrawn {
  /** Its name in the scene: its species and its place among the day's of it. */
  id: string;
  species: Deep7SpeciesId;
  forkCm: number;
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
  /** Whether it is fading out, gone from the day's count. */
  leaving: boolean;
}

interface Shown {
  species: Deep7SpeciesId;
  index: number;
  forkCm: number;
  wanted: boolean;
  alpha: number;
  /** Its place round its ring, radians, and which way it goes (+1 or -1). */
  turn: number;
  way: number;
  right: boolean | null;
  drawn: { middle: THREE.Vector3; width: number; height: number } | null;
}

/** A species' place in the scene's order by depth: its depths' middle's share between the shallowest's and the deepest's. */
function depthShare(species: Deep7SpeciesId): number {
  const mid = (s: (typeof DEEP7_SPECIES)[number]): number => (s.depthM[0] + s.depthM[1]) / 2;
  const mids = DEEP7_SPECIES.map(mid);
  const [lo, hi] = [Math.min(...mids), Math.max(...mids)];
  return (mid(DEEP7_SPECIES.find((s) => s.id === species) as (typeof DEEP7_SPECIES)[number]) - lo) / Math.max(1e-9, hi - lo);
}

/** The Deep 7 drawn on a benthic scene's floor, about its home view's middle. */
export class SlopeFish {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects = new Map<Deep7SpeciesId, readonly [number, number, number, number]>();
  private readonly shown = new Map<string, Shown>();
  /**
   * Each species' fish its ring is sized for, eased toward the day's count over a fade (review 29: sized on the fish
   * shown, every fish of a species jumped outward the frame a new one began fading in, and inward when one was gone).
   */
  private readonly ringN = new Map<Deep7SpeciesId, number>();
  private readonly along: THREE.Vector3;
  private readonly across: THREE.Vector3;
  private camera: THREE.Camera | null = null;
  private seconds = 0;
  /**
   * Whether the fish have swum since they were last drawn: drawn once a frame, by `face` (the scene's `fit`, after its
   * `animate`), or before it by whatever reads them first (a follow's), not again by each.
   */
  private swum = false;

  constructor(private readonly water: { middle: THREE.Vector3; eye: THREE.Vector3; floorAt: (x: number, z: number) => number }) {
    const ids = DEEP7_SPECIES.map((s) => s.id);
    const { texture, rects } = fishAtlas(ids.map((id) => DEEP7_SPRITES[id]));
    for (const id of ids) this.rects.set(id, rects.get(DEEP7_SPRITES[id]) as [number, number, number, number]);
    this.batch = new FishBatch(texture, true, true, true);
    this.batch.mesh.renderOrder = 3.9;
    const depth = this.batch.depthPass as THREE.Mesh;
    depth.renderOrder = 3.95;
    this.group.add(this.batch.mesh, depth);
    const look = new THREE.Vector3(water.middle.x - water.eye.x, 0, water.middle.z - water.eye.z);
    this.along = look.lengthSq() > 1e-12 ? look.normalize() : new THREE.Vector3(1, 0, 0);
    this.across = new THREE.Vector3(-this.along.z, 0, this.along.x);
  }

  /** The day's fish: each new one fading in at a place of its own on its ring, each gone fading out. */
  set(counts: readonly SlopeFishCount[]): void {
    const want = new Set<string>();
    for (const c of counts) {
      for (let index = 0; index < c.count; index++) {
        const id = `${c.species}:${index}`;
        want.add(id);
        const was = this.shown.get(id);
        if (was) [was.wanted, was.forkCm] = [true, c.forkCm];
        else {
          const k = DEEP7_SPECIES.findIndex((s) => s.id === c.species);
          this.shown.set(id, { species: c.species, index, forkCm: c.forkCm, wanted: true, alpha: 0, turn: 2 * Math.PI * hash3(k, index, 215), way: hash3(k, index, 216) < 0.5 ? -1 : 1, right: null, drawn: null });
        }
      }
    }
    for (const [id, s] of this.shown) if (!want.has(id)) s.wanted = false;
    // A species first seen sized at once for its count; one already there eased to it as it swims.
    for (const c of counts) if (!this.ringN.has(c.species)) this.ringN.set(c.species, c.count);
    this.draw();
  }

  /** A fish's ring's radius, m: `ringM` for each of its species' fish's square root, its own share of it. */
  private ring(s: Shown, k: number): number {
    return SLOPE_FISH.ringM * Math.sqrt(Math.max(1, this.ringN.get(s.species) ?? 1)) * (0.3 + 0.7 * hash3(k, s.index, 217));
  }

  /** A fish's middle on its ring now. */
  private at(s: Shown): THREE.Vector3 {
    const f = SLOPE_FISH;
    const k = DEEP7_SPECIES.findIndex((x) => x.id === s.species);
    // Its group's point: by its depth along the view, by its own draw across it.
    const along = -f.alongM / 3 + f.alongM * depthShare(s.species);
    const across = (2 * hash3(k, 1, 215) - 1) * f.acrossM;
    const cx = this.water.middle.x + this.along.x * along + this.across.x * across;
    const cz = this.water.middle.z + this.along.z * along + this.across.z * across;
    const ring = this.ring(s, k);
    const x = cx + ring * Math.cos(s.turn);
    const z = cz + ring * Math.sin(s.turn);
    const [lo, hi] = s.species === 'kalekale' ? f.lowM : f.aboveM;
    const above = lo + (hi - lo) * hash3(k, s.index, 218) + f.bobM * Math.sin((2 * Math.PI * this.seconds) / f.bobS + 6 * hash3(k, s.index, 219));
    return new THREE.Vector3(x, this.water.floorAt(x, z) + Math.max(0.3, above), z);
  }

  /** Moved on by `seconds` round their rings, each faded toward being there or gone; still when `calm`. */
  swim(seconds: number, calm = false): void {
    if (!calm) this.seconds += seconds;
    const wanted = new Map<Deep7SpeciesId, number>();
    for (const s of this.shown.values()) if (s.wanted) wanted.set(s.species, (wanted.get(s.species) ?? 0) + 1);
    const step = seconds / SLOPE_FISH.fadeS;
    for (const [species, n] of this.ringN) {
      const to = wanted.get(species) ?? 0;
      const next = n + Math.max(-step, Math.min(step, to - n));
      if (next <= 0 && to === 0) this.ringN.delete(species);
      else this.ringN.set(species, next);
    }
    for (const [id, s] of this.shown) {
      s.alpha = s.wanted ? Math.min(1, s.alpha + seconds / SLOPE_FISH.fadeS) : s.alpha - seconds / SLOPE_FISH.fadeS;
      if (s.alpha <= 0 && !s.wanted) {
        this.shown.delete(id);
        continue;
      }
      if (calm) continue;
      const lengthM = (s.forkCm / 100) * VISITOR_DRAW.fishTimes;
      const k = DEEP7_SPECIES.findIndex((x) => x.id === s.species);
      const ring = this.ring(s, k);
      s.turn += (s.way * seconds * SLOPE_FISH.lengthsPerS * lengthM) / Math.max(0.1, ring);
    }
    this.swum = true;
  }

  /** Each turned the way `camera` sees it swim. */
  face(camera: THREE.Camera): void {
    this.camera = camera;
    this.draw();
  }

  private draw(): void {
    this.swum = false;
    const draws: FishDraw[] = [];
    for (const s of this.shown.values()) {
      const at = this.at(s);
      // Its way: along its ring, the way it goes.
      const way = { x: -Math.sin(s.turn) * s.way, z: Math.cos(s.turn) * s.way };
      const right = facesRight(this.camera, at, way, s.right ?? way.x > 0);
      s.right = right;
      const sprite = DEEP7_SPRITES[s.species];
      const width = (s.forkCm / 100) * VISITOR_DRAW.fishTimes;
      const height = (width * sprite.height) / sprite.width;
      s.drawn = { middle: at, width, height };
      draws.push({ x: at.x, y: at.y - height / 2, z: at.z, width, height, rect: this.rects.get(s.species) as [number, number, number, number], right, alpha: Math.max(0, s.alpha) });
    }
    this.batch.set(draws);
  }

  /** The fish as drawn now. */
  drawnNow(): SlopeFishDrawn[] {
    if (this.swum) this.draw();
    return [...this.shown].flatMap(([id, s]) => (s.drawn ? [{ id, species: s.species, forkCm: s.forkCm, ...s.drawn, alpha: Math.max(0, s.alpha), leaving: !s.wanted }] : []));
  }

  /** The fish under a point on the screen (normalized device coordinates), by its name; null for none. */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): SlopeFishDrawn | null {
    return pickNearest(this.drawnNow(), camera, x, y, widthPx, heightPx, withinPx);
  }

  /** A fish by its name as drawn now, or null where none is. */
  drawnOf(id: string): SlopeFishDrawn | null {
    return this.drawnNow().find((d) => d.id === id) ?? null;
  }

  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** What the batch draws, for the tests. */
  draws(): FishDraw[] {
    if (this.swum) this.draw();
    return this.batch.drawn();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
